import React from 'react';

// Container / Row / Col — lightweight take on Bootstrap's 12-col flex grid, tw- prefixed.
type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface ContainerProps extends DivProps {
  fluid?: boolean;
}
export const Container: React.FC<ContainerProps> = ({ fluid, className = '', children, ...rest }) => (
  <div className={`${fluid ? 'tw-w-full' : 'tw-w-full tw-max-w-screen-xl tw-mx-auto'} tw-px-4 ${className}`} {...rest}>
    {children}
  </div>
);

export const Row: React.FC<DivProps> = ({ className = '', children, ...rest }) => (
  <div className={`tw-flex tw-flex-wrap -tw-mx-2 ${className}`} {...rest}>{children}</div>
);

type ColSize = boolean | number | 'auto';
export interface ColProps extends DivProps {
  xs?: ColSize; sm?: ColSize; md?: ColSize; lg?: ColSize; xl?: ColSize; xxl?: ColSize;
}

// Static class maps so Tailwind's scanner sees every literal (dynamic strings aren't detected).
const W: Record<number, string> = {
  1: 'tw-w-1/12', 2: 'tw-w-2/12', 3: 'tw-w-3/12', 4: 'tw-w-4/12', 5: 'tw-w-5/12', 6: 'tw-w-6/12',
  7: 'tw-w-7/12', 8: 'tw-w-8/12', 9: 'tw-w-9/12', 10: 'tw-w-10/12', 11: 'tw-w-11/12', 12: 'tw-w-full',
};
const W_SM: Record<number, string> = {
  1: 'sm:tw-w-1/12', 2: 'sm:tw-w-2/12', 3: 'sm:tw-w-3/12', 4: 'sm:tw-w-4/12', 5: 'sm:tw-w-5/12', 6: 'sm:tw-w-6/12',
  7: 'sm:tw-w-7/12', 8: 'sm:tw-w-8/12', 9: 'sm:tw-w-9/12', 10: 'sm:tw-w-10/12', 11: 'sm:tw-w-11/12', 12: 'sm:tw-w-full',
};
const W_MD: Record<number, string> = {
  1: 'md:tw-w-1/12', 2: 'md:tw-w-2/12', 3: 'md:tw-w-3/12', 4: 'md:tw-w-4/12', 5: 'md:tw-w-5/12', 6: 'md:tw-w-6/12',
  7: 'md:tw-w-7/12', 8: 'md:tw-w-8/12', 9: 'md:tw-w-9/12', 10: 'md:tw-w-10/12', 11: 'md:tw-w-11/12', 12: 'md:tw-w-full',
};
const W_LG: Record<number, string> = {
  1: 'lg:tw-w-1/12', 2: 'lg:tw-w-2/12', 3: 'lg:tw-w-3/12', 4: 'lg:tw-w-4/12', 5: 'lg:tw-w-5/12', 6: 'lg:tw-w-6/12',
  7: 'lg:tw-w-7/12', 8: 'lg:tw-w-8/12', 9: 'lg:tw-w-9/12', 10: 'lg:tw-w-10/12', 11: 'lg:tw-w-11/12', 12: 'lg:tw-w-full',
};
const W_XL: Record<number, string> = {
  1: 'xl:tw-w-1/12', 2: 'xl:tw-w-2/12', 3: 'xl:tw-w-3/12', 4: 'xl:tw-w-4/12', 5: 'xl:tw-w-5/12', 6: 'xl:tw-w-6/12',
  7: 'xl:tw-w-7/12', 8: 'xl:tw-w-8/12', 9: 'xl:tw-w-9/12', 10: 'xl:tw-w-10/12', 11: 'xl:tw-w-11/12', 12: 'xl:tw-w-full',
};
const W_2XL: Record<number, string> = {
  1: '2xl:tw-w-1/12', 2: '2xl:tw-w-2/12', 3: '2xl:tw-w-3/12', 4: '2xl:tw-w-4/12', 5: '2xl:tw-w-5/12', 6: '2xl:tw-w-6/12',
  7: '2xl:tw-w-7/12', 8: '2xl:tw-w-8/12', 9: '2xl:tw-w-9/12', 10: '2xl:tw-w-10/12', 11: '2xl:tw-w-11/12', 12: '2xl:tw-w-full',
};

const cls = (map: Record<number, string>, flex1: string, v?: ColSize): string => {
  if (v === undefined) return '';
  if (v === true || v === 'auto') return flex1;
  if (typeof v === 'number') return map[Math.min(Math.max(v, 1), 12)] || '';
  return '';
};

export const Col: React.FC<ColProps> = ({ xs, sm, md, lg, xl, xxl, className = '', children, ...rest }) => {
  const none = [xs, sm, md, lg, xl, xxl].every((v) => v === undefined);
  const classNames = [
    'tw-px-2 tw-w-full',
    none ? 'tw-flex-1' : '',
    cls(W, 'tw-flex-1', xs),
    cls(W_SM, 'sm:tw-flex-1', sm),
    cls(W_MD, 'md:tw-flex-1', md),
    cls(W_LG, 'lg:tw-flex-1', lg),
    cls(W_XL, 'xl:tw-flex-1', xl),
    cls(W_2XL, '2xl:tw-flex-1', xxl),
    className,
  ].filter(Boolean).join(' ');
  return <div className={classNames} {...rest}>{children}</div>;
};

export default Container;
